import { Badge, Button, Card, CardBody, DataTable, highlightText, Icon } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useSpinDelay } from 'spin-delay';
import { MyReviewApplicant, useMyReviews } from '../../../Contexts/MyReviewsContext';
import { useAsyncEffect } from '../../../Hooks/AsyncEffect';
import { useLocalizedStrings } from '../../../Localization';
import { MyReviewsFilter } from './MyReviewsFilter';
import type { MyReviews } from '@aidkitorg/types/lib/survey';

interface MyReviewsDataTableProps {
  pageConfig?: MyReviews;
  showSearchBar?: boolean;
}

export const MyReviewsDataTable: React.FC<MyReviewsDataTableProps> = ({ pageConfig, showSearchBar = true }) => {
  const L = useLocalizedStrings();

  const {
    applicants,
    isLoading,
    isSearching,
    getMyReviews,
    total,
    page,
    limit,
    totalPages,
    search,
    searchDisplay,
    sorting,
    filters,
    onPaginationChange,
    onSearchChange,
    onSortingChange
  } = useMyReviews();

  // Track if initial data has been loaded
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // Use spin-delay to prevent loading flashes - only show loading after 250ms
  const showLoadingSpinner = useSpinDelay(isLoading && hasInitialLoad, {
    delay: 250,
    minDuration: 150
  });

  const showSearchSpinner = useSpinDelay(isSearching && hasInitialLoad, {
    delay: 250,
    minDuration: 150
  });

  useAsyncEffect(async () => {
    await getMyReviews();
    setHasInitialLoad(true);
  }, []);

  const MY_REVIEWS_COLUMNS: ColumnDef<MyReviewApplicant>[] = React.useMemo(() => [
    {
      id: 'uid',
      accessorKey: 'uid',
      header: L.admin_panel.reviews.uid,
      enableSorting: true,
      enablePinning: true,
      cell: ({ row }) => {
        const uid = row.original.uid;
        const [copied, setCopied] = useState(false);

        const handleCopy = async () => {
          try {
            await navigator.clipboard.writeText(uid);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch (err) {
            console.error("Failed to copy: ", err);
          }
        };

        return (
          <div className="flex items-center gap-2">
            <span className="truncate">
              {(!isSearching && searchDisplay && searchDisplay === search)
                ? highlightText(uid, searchDisplay)
                : uid}
            </span>
            <Button
              icon={<Icon role="decorative" name={copied ? "CheckIcon" : "ClipboardDocumentIcon"}/>}
              variant="secondary"
              size="sm"
              aria-label={L.admin_panel.reviews.copy_uid}
              onClick={handleCopy}
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'legal_name',
      header: L.admin_panel.reviews.name,
      enableSorting: true,
      cell: ({ row }) => {
        const name = row.original.legal_name || '--';
        return (
          <span>
            {(!isSearching && searchDisplay && searchDisplay === search)
              ? highlightText(name, searchDisplay)
              : name}
          </span>
        );
      }
    },
    {
      accessorKey: 'queue',
      header: L.admin_panel.reviews.queue,
      enableSorting: false,
      cell: ({ row }) => {
        const queue = row.original.queue;
        if (!queue) {
          return <span className="text-gray-400">--</span>;
        }
        return (
          <span className="text-sm">
            {queue.name}
          </span>
        );
      }
    },
    {
      accessorKey: 'assigned_at',
      header: L.admin_panel.reviews.assigned_at,
      enableSorting: true,
      cell: ({ row }) => {
        if (!row.original.assigned_at) {
          return <span className="text-gray-400">--</span>;
        }
        const date = new Date(row.original.assigned_at);
        const formatted = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        }).format(date);

        return <span className="text-sm">{formatted}</span>;
      },
    },
    {
      accessorKey: 'review_state',
      header: L.admin_panel.reviews.review_state,
      enableSorting: false,
      cell: ({ row }) => {
        const state = row.original.review_state;

        // Queue doesn't support status tracking
        if (state === 'N/A') {
          return <Badge variant="neutral">N/A</Badge>;
        }

        // Complete status
        if (state === 'complete') {
          return <Badge variant="success">{L.admin_panel.reviews.completed}</Badge>;
        }

        // In Progress status
        if (state === 'in_progress') {
          return <Badge variant="info">{L.admin_panel.reviews.in_review}</Badge>;
        }

        // Fallback
        return <span className="text-gray-400">--</span>;
      }
    },
    {
      id: 'view',
      header: L.admin_panel.reviews.view_application,
      enableSorting: false,
      enablePinning: true,
      cell: ({ row }) => {
        // Use the queue's startSection, or default to 'review'
        const startSection = row.original.queue?.startSection || 'review';
        // Normalize the section path (remove leading slash if present)
        const normalizedSection = startSection.startsWith('/') ? startSection.slice(1) : startSection;

        return (
          <NavLink
            to={`/admin-panel/a/${row.original.uid}/${normalizedSection}`}
            target='_blank'
            className="text-[var(--ak-color-typography-link)] hover:text-[var(--ak-color-typography-link-hover)] font-medium inline-flex items-center gap-1"
          >
            {L.admin_panel.reviews.view} <Icon size="sm" role="decorative" name="ArrowTopRightOnSquareIcon" />
          </NavLink>
        );
      }
    }
  ], [L, isSearching, searchDisplay, search]);

  // Filter columns based on pageConfig - remove columns that are set to 'hidden'
  const visibleColumns = useMemo(() => {
    if (!pageConfig?.columns) {
      // If no config, show nothing
      return [];
    }

    const config = pageConfig.columns;
    return MY_REVIEWS_COLUMNS.filter((column) => {
      const id = ('id' in column && column.id) || ('accessorKey' in column && column.accessorKey) as string;

      // Always show these columns
      if (id === 'uid' || id === 'view') return true;

      // Check config for other columns
      if (id === 'legal_name') return config.name !== 'hidden';
      if (id === 'queue') return config.queue !== 'hidden';
      if (id === 'assigned_at') return config.assignedAt !== 'hidden';
      if (id === 'review_state') return config.reviewState !== 'hidden';

      // Show all other columns by default
      return true;
    });
  }, [pageConfig, MY_REVIEWS_COLUMNS]);

  // Calculate active filter count
  const activeFilters = (filters.queue.length > 0 ? 1 : 0) + (filters.reviewState.length > 0 ? 1 : 0);

  // Determine if there are any available filters based on pageConfig
  const hasAvailableFilters = useMemo(() => {
    if (!pageConfig?.columns) {
      // If no config, all filters are available by default
      return true;
    }

    const columns = pageConfig.columns;
    // Check if at least one filterable column is visibleAndFilterable
    return columns.queue === 'visibleAndFilterable' ||
           columns.reviewState === 'visibleAndFilterable';
  }, [pageConfig]);

  return (
    <Card className="rounded-md h-full w-full">
      <CardBody>
        <div className="relative">
          <DataTable
            columns={visibleColumns}
            data={applicants}
            enableSearch={showSearchBar}
            searchLabel={L.admin_panel.reviews.search_placeholder}
            dataFilteredOutMessage={L.admin_panel.reviews.no_results_for_search}
            noDataMessage={L.admin_panel.reviews.no_applicants_assigned}
            initializing={!hasInitialLoad}
            loading={showLoadingSpinner || showSearchSpinner}
            showPagination
            pageSize={limit}
            columnPinningState={{
              left: ['uid'],
              right: ['view']
            }}
            filters={hasAvailableFilters ? () => (
              <MyReviewsFilter pageConfig={pageConfig} />
            ) : undefined}
            activeFilters={activeFilters}
            manualSearch={showSearchBar ? {
              searchValue: searchDisplay,
              onSearchChange: onSearchChange,
              resetPageOnChange: false
            } : undefined}
            manualPagination={{
              pageCount: totalPages || 0,
              pageIndex: Math.max(0, page - 1),
              totalRows: total || 0,
              onPaginationChange: onPaginationChange
            }}
            manualSorting={{
              sorting: sorting,
              onSortingChange: onSortingChange
            }}
          />
        </div>
      </CardBody>
    </Card>
  );
};
